import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import {
    Campaign as CampaignIcon,
    Email as EmailIcon,
    Forum as ForumIcon,
    Launch as LaunchIcon,
    LocalOffer as OfferIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Store as StoreIcon,
    Tv as TvIcon,
    Work as WorkIcon
} from '@mui/icons-material';
import AgentPolicyContent from './common/AgentPolicyContent';
import { useAuth } from '../contexts/AuthContext';

const SECTION_KEYS = {
    announcements: 'Announcements / Hot Deals',
    primary: 'Primary Quick Links',
    epp: 'EPP Tools',
    store: 'Store Info',
    sales: 'Sales Tools',
    training: 'Training Shortcuts',
    backup: 'Backup Tools'
};

export function normalizeDashboardRoute(url = '') {
    if (url === '/offers/send-offer') {
        return '/offers/send';
    }
    if (url === '/offers/tv-calculator') {
        return '/offers/tv-packages-v2';
    }
    return url || '/dashboard';
}

function getIcon(iconType) {
    switch (iconType) {
        case 'email':
            return <EmailIcon color="primary" />;
        case 'phone':
            return <PhoneIcon color="primary" />;
        case 'store':
            return <StoreIcon color="primary" />;
        case 'tv':
            return <TvIcon color="primary" />;
        case 'crm':
        case 'form':
        case 'sheet':
            return <WorkIcon color="primary" />;
        default:
            return <OfferIcon color="primary" />;
    }
}

function sortByPinnedAndOrder(items = []) {
    return [...items].sort((a, b) => {
        if (Boolean(a.pinned) !== Boolean(b.pinned)) {
            return a.pinned ? -1 : 1;
        }
        return Number(a.sortOrder || 999) - Number(b.sortOrder || 999);
    });
}

function matchesSection(link, section) {
    return link.section === section.title || link.section === SECTION_KEYS[section.key];
}

function LinkCard({ link, onOpen }) {
    const primary = Boolean(link.primaryAction);

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                borderColor: primary ? 'primary.main' : 'divider',
                bgcolor: primary ? 'rgba(25, 118, 210, 0.06)' : 'background.paper'
            }}
        >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    {getIcon(link.iconType)}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                        {link.label}
                    </Typography>
                    {link.pinned && <Chip label="Pinned" size="small" color="primary" variant="outlined" />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {link.description}
                </Typography>
                <Button
                    variant={primary ? 'contained' : 'outlined'}
                    size="small"
                    endIcon={link.openInNewTab ? <LaunchIcon /> : null}
                    onClick={() => onOpen(link)}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    Open
                </Button>
            </CardContent>
        </Card>
    );
}

function AnnouncementCard({ item, onOpen }) {
    return (
        <Card variant="outlined" sx={{ borderColor: item.pinned ? 'primary.main' : 'divider' }}>
            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <CampaignIcon color="primary" />
                            <Typography variant="h6" fontWeight={700}>
                                {item.title}
                            </Typography>
                            {item.badgeText && (
                                <Chip label={item.badgeText} color={item.badgeColor || 'default'} size="small" />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {item.description}
                        </Typography>
                    </Box>
                    {item.ctaText && item.url && (
                        <Button
                            variant="contained"
                            endIcon={item.openInNewTab ? <LaunchIcon /> : null}
                            onClick={() => onOpen(item)}
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, whiteSpace: 'nowrap' }}
                        >
                            {item.ctaText}
                        </Button>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function StoreInfoCard({ storeInfo, storeLinks, onOpen }) {
    if (!storeInfo && storeLinks.length === 0) {
        return null;
    }

    return (
        <Grid container spacing={2}>
            {storeInfo && (
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <LocationIcon color="primary" />
                                <Typography variant="h6" fontWeight={700}>
                                    {storeInfo.storeName}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {storeInfo.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {storeInfo.phone}
                            </Typography>
                            {storeInfo.supportNote && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {storeInfo.supportNote}
                                </Typography>
                            )}
                            {storeInfo.mapUrl && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ mt: 2 }}
                                    endIcon={<LaunchIcon />}
                                    onClick={() => onOpen({ url: storeInfo.mapUrl, internalRoute: false, openInNewTab: true })}
                                >
                                    Open Map
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            )}
            {storeLinks.map((link) => (
                <Grid item xs={12} md={6} key={link.id}>
                    <LinkCard link={link} onOpen={onOpen} />
                </Grid>
            ))}
        </Grid>
    );
}

function AgentDashboardHome({
    dashboardOverride = null,
    previewMode = false,
    policyStatusOverride = null,
    escalationsAvailability = null,
    escalationsAvailabilityLoaded = false
}) {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [dashboard, setDashboard] = useState(dashboardOverride);
    const [policyStatus, setPolicyStatus] = useState(null);
    const [policyReviewOpen, setPolicyReviewOpen] = useState(false);
    const [escalationSummary, setEscalationSummary] = useState(null);
    const [loading, setLoading] = useState(!dashboardOverride);
    const [error, setError] = useState('');

    useEffect(() => {
        if (dashboardOverride) {
            setDashboard(dashboardOverride);
            setPolicyStatus(policyStatusOverride);
            setLoading(false);
            return;
        }

        let ignore = false;
        const loadDashboard = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await fetch('/api/agent-dashboard/active');
                const data = await response.json();
                if (!ignore) {
                    if (data.success) {
                        setDashboard(data.dashboard);
                        setPolicyStatus(data.policyStatus || null);
                    } else {
                        setError(data.error || 'Failed to load dashboard.');
                    }
                }
            } catch (loadError) {
                if (!ignore) {
                    setError('Failed to connect to dashboard service.');
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        loadDashboard();
        return () => {
            ignore = true;
        };
    }, [dashboardOverride, policyStatusOverride]);

    const showEscalationsCard = Boolean(
        escalationsAvailabilityLoaded && escalationsAvailability?.availableToCurrentUser
    );

    useEffect(() => {
        if (!showEscalationsCard || currentUser?.isAdmin) {
            setEscalationSummary(null);
            return;
        }

        let ignore = false;
        const loadEscalationSummary = async () => {
            try {
                const response = await fetch('/api/escalations/mine?status=submitted&page=1&limit=1');
                const data = await response.json();
                if (!ignore && response.ok && data.success) {
                    setEscalationSummary(data.summary || null);
                }
            } catch (error) {
                if (!ignore) {
                    setEscalationSummary(null);
                }
            }
        };

        loadEscalationSummary();
        return () => {
            ignore = true;
        };
    }, [currentUser?.isAdmin, showEscalationsCard]);

    const sections = useMemo(() => [...(dashboard?.sections || [])].sort((a, b) => Number(a.sortOrder || 999) - Number(b.sortOrder || 999)), [dashboard]);
    const quickLinks = useMemo(() => sortByPinnedAndOrder(dashboard?.quickLinks || []), [dashboard]);
    const announcements = useMemo(() => sortByPinnedAndOrder(dashboard?.announcements || []), [dashboard]);
    const activePolicy = policyStatus?.activePolicy || null;
    const showPolicyCard = Boolean(activePolicy);
    const policyCardColor = policyStatus?.requiresAcknowledgement ? 'warning' : (policyStatus?.exempt ? 'info' : 'success');
    const policyCardLabel = policyStatus?.requiresAcknowledgement
        ? 'Pending acknowledgement'
        : (policyStatus?.exempt ? 'Admin preview' : 'Acknowledged');

    const handleOpen = (item) => {
        if (!item?.url) {
            return;
        }
        if (item.internalRoute) {
            navigate(normalizeDashboardRoute(item.url));
            return;
        }
        window.open(item.url, item.openInNewTab === false ? '_self' : '_blank', 'noopener,noreferrer');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            {previewMode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Previewing Agent Dashboard
                </Alert>
            )}

            <Box mb={3}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    Agent Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Today&apos;s offers, shortcuts, and store tools for agents.
                </Typography>
            </Box>

            <Stack spacing={3}>
                {showEscalationsCard && (
                    <Paper sx={{ p: 2.5, borderLeft: '4px solid', borderColor: 'primary.main' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                                    <ForumIcon color="primary" />
                                    <Typography variant="h6" color="primary" fontWeight={700}>
                                        Escalations
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {currentUser?.isAdmin
                                        ? 'Admin preview: Escalations remain available to administrators while agent access is disabled.'
                                        : `Submitted ${Number(escalationSummary?.statusCounts?.submitted || 0)} · Pending ${Number(escalationSummary?.statusCounts?.pending || 0)}`}
                                </Typography>
                            </Box>
                            <Button variant="contained" onClick={() => navigate('/escalations')}>
                                {currentUser?.isAdmin ? 'Open Escalations' : 'New Escalation'}
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {showPolicyCard && (
                    <Paper sx={{ p: 2.5, borderLeft: '4px solid', borderColor: `${policyCardColor}.main` }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                                    <Typography variant="h6" color="primary" fontWeight={700}>
                                        Company Policy & Work Rules
                                    </Typography>
                                    <Chip
                                        size="small"
                                        color={policyCardColor}
                                        label={policyCardLabel}
                                    />
                                    <Chip size="small" variant="outlined" label={`Version ${activePolicy.version}`} />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {policyStatus?.requiresAcknowledgement
                                        ? 'Please review and acknowledge the current policy before continuing.'
                                        : (policyStatus?.exempt
                                            ? 'Admin preview only. Real active agents must acknowledge the current published policy.'
                                            : `Current policy acknowledged${policyStatus?.acknowledgement?.acknowledgedAt ? ` on ${policyStatus.acknowledgement.acknowledgedAt}` : ''}.`)}
                                </Typography>
                            </Box>
                            <Button variant="outlined" onClick={() => setPolicyReviewOpen(true)}>
                                Review Policy
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {sections.map((section) => {
                    if (section.key === 'announcements') {
                        if (announcements.length === 0) return null;
                        return (
                            <Paper key={section.key} sx={{ p: 2.5 }}>
                                <Typography variant="h6" color="primary" gutterBottom>
                                    {section.title}
                                </Typography>
                                <Stack spacing={2}>
                                    {announcements.map((item) => (
                                        <AnnouncementCard key={item.id} item={item} onOpen={handleOpen} />
                                    ))}
                                </Stack>
                            </Paper>
                        );
                    }

                    if (section.key === 'store') {
                        const storeLinks = quickLinks.filter((link) => matchesSection(link, section));
                        if (!dashboard?.storeInfo && storeLinks.length === 0) return null;
                        return (
                            <Paper key={section.key} sx={{ p: 2.5 }}>
                                <Typography variant="h6" color="primary" gutterBottom>
                                    {section.title}
                                </Typography>
                                <StoreInfoCard storeInfo={dashboard?.storeInfo} storeLinks={storeLinks} onOpen={handleOpen} />
                            </Paper>
                        );
                    }

                    const links = quickLinks.filter((link) => matchesSection(link, section));
                    if (links.length === 0) return null;

                    return (
                        <Paper key={section.key} sx={{ p: 2.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6" color="primary">
                                    {section.title}
                                </Typography>
                                <Divider sx={{ flexGrow: 1, ml: 2 }} />
                            </Box>
                            <Grid container spacing={2}>
                                {links.map((link) => (
                                    <Grid item xs={12} sm={6} md={links.some((item) => item.primaryAction) ? 4 : 3} key={link.id}>
                                        <LinkCard link={link} onOpen={handleOpen} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    );
                })}
            </Stack>

            <Dialog open={policyReviewOpen} onClose={() => setPolicyReviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{activePolicy?.title || 'Company Policy & Work Rules'}</DialogTitle>
                <DialogContent dividers>
                    <AgentPolicyContent html={activePolicy?.html || ''} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPolicyReviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AgentDashboardHome;
